import { DatePipe } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, ViewChild, effect, inject, signal } from '@angular/core';
import { ENVIRONMENT } from '@assethub/shared/models';
import { ConsentService, LicensingService } from '@assethub/shared/services';
import { Logger } from '@assethub/shared/utils';
import { OAuthInfoEvent, OAuthService } from 'angular-oauth2-oidc';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';

interface MyAccount {
  uuid: string;
  attributes: {
    admin?: 'operator' | 'admin';
    developer?: boolean;
    licenseAssetHub?: string;
    licenseLiveConnect?: null;
    licenseServiceModule?: null;
  };
}

@Component({
  selector: 'app-self-diagnosis',
  templateUrl: './self-diagnosis.component.html',
  styleUrls: ['./self-diagnosis.component.scss'],
  imports: [DatePipe, TextareaModule, ButtonModule],
})
export class SelfDiagnosisComponent {
  currentLogin = signal('');
  name = signal('');
  customernumber = signal('');
  subsidiaryCode = signal('');
  tokenIssuedAt = signal(new Date());
  tokenExpire = signal(new Date());
  customerId = signal('');
  now = signal(new Date());
  token = signal('');
  consents = signal('');
  overrideAssetHub = signal('');
  overrideLiveConnect = signal('');
  privileges = signal('');

  @ViewChild('bearerToken')
  bearerTokenTextbox: ElementRef<HTMLTextAreaElement>;

  private logger = new Logger(this.constructor.name);
  private apiUrl = inject(ENVIRONMENT).apiUrl;

  constructor(
    private auth: OAuthService,
    public licensingService: LicensingService,
    private httpClient: HttpClient,
    private consentService: ConsentService,
  ) {
    this.updateToken();
    effect(onCleanup => {
      const subscription = this.auth.events.subscribe({
        next: (event: OAuthInfoEvent) => {
          if (event.type === 'token_refreshed') {
            this.updateToken();
          }
        },
      });
      onCleanup(() => subscription.unsubscribe());
    });

    this.consentService.fetchConsents().subscribe({
      next: countOpen => {
        if (countOpen) {
          this.consents.set(`ok, pending consents for ${countOpen} services`);
        } else {
          this.consents.set(`ok, no pending consents`);
        }
      },
      error: err => {
        this.logger.error(err);
        this.consents.set(this.formatError(err));
      },
    });

    this.httpClient.get<MyAccount>(this.apiUrl + '/my-account').subscribe({
      next: ok => {
        this.logger.debug('my account:', ok);
        this.overrideAssetHub.set(ok.attributes?.licenseAssetHub ? '*' : '');
        this.overrideLiveConnect.set('licenseLiveConnect' in ok.attributes ? '*' : '');
        this.privileges.set(ok.attributes?.admin || 'none');
      },
      error: err => {
        this.logger.error(err);
      },
    });
  }

  copyBearerToken() {
    if (!this.bearerTokenTextbox) {
      return;
    }
    const element = this.bearerTokenTextbox.nativeElement;
    element.select();
    element.setSelectionRange(0, 99999);
    navigator.clipboard.writeText(element.value);
  }

  private updateToken() {
    this.token.set(this.auth.getIdToken());
    const claims = this.auth.getIdentityClaims();
    this.currentLogin.set(claims['preferred_username'] || claims['email'] || '');
    this.name.set(claims['name'] || '');
    this.customernumber.set(claims['customernumber'] || '');
    this.subsidiaryCode.set(claims['ssucode'] || '');
    this.tokenExpire.set(new Date(claims['exp'] * 1000));
    this.tokenIssuedAt.set(new Date(claims['iat'] * 1000));
    this.now.set(new Date());
    this.customerId.set(claims['sub'] || '');
  }

  private formatError(err): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return err.message;
      } else {
        return `${err.message}: (${err.error?.message})`;
      }
    }
    return err.toString();
  }
}
